import { Box, chakra, Flex, Icon, Text, TextProps } from '@chakra-ui/react';
import { Tooltip } from '../ui/tooltip';
import { ENVIRONMENT } from '@monetize/ui/config';
import React, { FunctionComponent as FC, useState } from 'react';
import { MdCheckCircle } from 'react-icons/md';
import { useNavigatorPermission } from '../../hooks/useNavigatorPermission';
import { copyToClipboard } from '../../utils';
import MIconButton from './MIconButton';

interface MClipboardCopyBoxProps extends TextProps {
  copyValue: string;
  displayText?: string;
  fontSize?: string;
  isDisabled?: boolean;
  tooltipMessage?: string;
  renderContent?: null | (() => React.ReactElement);
  onCopyContent?: ({
    copyToClipboard,
    setCopiedContent,
    setShowCopyTooltip,
  }: {
    copyToClipboard: (str: string) => void;
    setCopiedContent: (val: boolean) => void;
    setShowCopyTooltip: (val: boolean) => void;
  }) => void;
}

export const MClipboardCopyBox: FC<MClipboardCopyBoxProps> = React.forwardRef<
  any,
  MClipboardCopyBoxProps
>(
  (
    {
      copyValue,
      displayText,
      fontSize = 'sm',
      isDisabled: isDisabledProp,
      tooltipMessage = 'Copy to Clipboard',
      renderContent,
      onCopyContent,
      ...rest
    }: MClipboardCopyBoxProps,
    ref,
  ) => {
    const [copiedContent, setCopiedContent] = useState<boolean>(false);
    const [showCopyTooltip, setShowCopyTooltip] = useState<boolean>(false);
    const { canCopyClipboard } = useNavigatorPermission();
    const isDisabled = isDisabledProp || !canCopyClipboard;

    const copyContent = (ev: React.MouseEvent<HTMLElement>) => {
      if (isDisabled) {
        return;
      }
      if (onCopyContent) {
        onCopyContent({
          copyToClipboard,
          setCopiedContent,
          setShowCopyTooltip,
        });
      } else {
        ev.stopPropagation();
        copyToClipboard(copyValue);
        setCopiedContent(true);
        setShowCopyTooltip(false);
        setTimeout(() => {
          setCopiedContent(false);
        }, 1000);
      }
    };

    const copiedLabel = (
      <Flex alignItems="center">
        <Icon w="4" h="4" mr="1" asChild>
          <MdCheckCircle />
        </Icon>
        <Text>Copied!</Text>
      </Flex>
    );

    // there is a bug with Chakra handling background colors of this tooltip that occurs only in test, so
    // this temporary workaround skips the custom color
    const bgColor =
      ENVIRONMENT.NODE_ENV === 'test' ? {} : { bg: 'tPurple.dark' };

    return (
      <Flex alignItems="center">
        <Box
          onMouseEnter={() => !isDisabled && setShowCopyTooltip(true)}
          onMouseLeave={() => !isDisabled && setShowCopyTooltip(false)}
        >
          {/*
           * A single tooltip whose content/openness swap between the hover
           * message and the post-copy confirmation. Two separate Tooltips
           * stacked on the same trigger (the pre-migration v2 shape) each
           * ran their own hover machine — Ark's tooltip auto-reopens a
           * second tooltip instantly (skipping its open delay AND its
           * controlled-open guard) whenever another tooltip is already
           * visible nearby, so hovering while "Copy to Clipboard" was
           * showing could force "Copied!" open too, stacking both. A single
           * trigger has no second tooltip to spuriously reopen.
           */}
          <Tooltip
            showArrow
            disabled={isDisabled}
            content={copiedContent ? copiedLabel : tooltipMessage}
            // Fully controlled: visibility timing (immediate hover, 1s
            // post-copy) is already owned by this component's own state.
            open={copiedContent || showCopyTooltip}
            positioning={{
              placement: 'top-start',
            }}
            contentProps={bgColor}
          >
            {!renderContent ? (
              <chakra.button
                type="button"
                aria-label={tooltipMessage}
                onClick={copyContent}
                disabled={isDisabled}
                cursor={!isDisabled ? 'pointer' : 'default'}
                display="inline-flex"
                alignItems="center"
                bg="transparent"
                border="none"
                p={0}
                textAlign="left"
                fontSize={fontSize as string | undefined}
                fontWeight="normal"
                color="tGray.darkGrayPurple"
                borderRadius="sm"
                _focusVisible={{
                  outline: 'none',
                  boxShadow: 'focus',
                }}
              >
                {displayText && (
                  <chakra.span fontWeight="bold" mr={1}>
                    {displayText}
                  </chakra.span>
                )}
              </chakra.button>
            ) : (
              <MIconButton
                size="xs"
                variant="icon"
                aria-label={tooltipMessage}
                onClick={copyContent}
                isDisabled={isDisabled}
                icon={renderContent()}
              />
            )}
          </Tooltip>
        </Box>
      </Flex>
    );
  },
);
